// NotificationsPage.js - Full Notifications Page Component
import React, { useState, useEffect } from "react";
import { Button, Empty, Spin, Select } from "antd";
import {
  Bell,
  ArrowLeft,
  CheckCheck,
  Trash2,
  Filter,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";
import { useNavigate } from "react-router-dom";
import NotificationItem from "./NotificationItem";
import HeaderComponent from "../Home/HeaderComponent";
import Footer from "../Home/Footer";

const { Option } = Select;

const NotificationsPage = () => {
  const [filters, setFilters] = useState({
    pageNumber: 1,
    is_read: "all",
  });
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const navigate = useNavigate();

  // API hooks
  const [notificationsResponse, getNotifications] = useApi();
  const [statsResponse, getStats] = useApi();
  const [markAllResponse, markAllAsRead] = useApi();
  const [markReadResponse, markAsRead] = useApi();
  const [deleteResponse, deleteNotification] = useApi();

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [filters]);

  const fetchNotifications = () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all") {
        queryParams.append(key, value);
      }
    });
    getNotifications(
      null,
      API_METHODS.GET,
      `/user/notifications?${queryParams.toString()}`
    );
  };

  const fetchStats = () => {
    getStats(null, API_METHODS.GET, "/user/notifications/stats");
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead({}, API_METHODS.PUT, "/user/notifications/mark-all");
  };

  const handleRefresh = () => {
    fetchNotifications();
    fetchStats();
  };

  const handleNotificationUpdate = () => {
    fetchNotifications();
    fetchStats();
  };

  useEffect(() => {
    if (markAllResponse.data && !markAllResponse.isLoading) {
      fetchNotifications();
      fetchStats();
    }
  }, [markAllResponse]);

  const notifications = notificationsResponse.data || [];
  const stats = statsResponse.data || {};
  const pagination = notificationsResponse.pagination || {};

  return (
    <>
      <HeaderComponent />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className="p-3 text-gray-600 hover:text-orange-600 transition-all duration-300 transform hover:scale-110 rounded-full hover:bg-orange-50"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-xl">
                  <Bell className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">
                    Notifications
                  </h1>
                  <p className="text-gray-600">
                    {stats.total_notifications > 0
                      ? `${stats.total_notifications} total • ${
                          stats.unread_count || 0
                        } unread`
                      : "No notifications yet"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                icon={<RefreshCw className="h-4 w-4" />}
                onClick={handleRefresh}
                loading={notificationsResponse.isLoading}
              >
                Refresh
              </Button>

              {stats.unread_count > 0 && (
                <Button
                  type="primary"
                  icon={<CheckCheck className="h-4 w-4" />}
                  onClick={handleMarkAllAsRead}
                  loading={markAllResponse.isLoading}
                  className="bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600"
                >
                  Mark All Read
                </Button>
              )}
            </div>
          </div>

          {/* Statistics Cards */}
          {stats.total_notifications > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl shadow-lg border border-orange-100 p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {stats.total_notifications}
                </div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg border border-orange-100 p-4 text-center">
                <div className="text-2xl font-bold text-red-600">
                  {stats.unread_count || 0}
                </div>
                <div className="text-sm text-gray-600">Unread</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg border border-orange-100 p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {stats.order_notifications || 0}
                </div>
                <div className="text-sm text-gray-600">Orders</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg border border-orange-100 p-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {stats.system_notifications || 0}
                </div>
                <div className="text-sm text-gray-600">System</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-orange-600" />
                <h3 className="font-semibold text-gray-800">
                  Filter Notifications
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <Select
                  value={filters.is_read}
                  onChange={(value) => handleFilterChange("is_read", value)}
                  className="w-full"
                  size="large"
                >
                  <Option value="all">All Notifications</Option>
                  <Option value="false">
                    <div className="flex items-center space-x-2">
                      <EyeOff className="h-4 w-4" />
                      <span>Unread Only</span>
                    </div>
                  </Option>
                  <Option value="true">
                    <div className="flex items-center space-x-2">
                      <Eye className="h-4 w-4" />
                      <span>Read Only</span>
                    </div>
                  </Option>
                </Select>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="bg-white rounded-2xl shadow-xl border border-orange-100 overflow-hidden">
            {notificationsResponse.isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spin size="large" />
                <span className="ml-4 text-lg text-gray-600">
                  Loading notifications...
                </span>
              </div>
            ) : notifications.length > 0 ? (
              <>
                {/* Notifications Header */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800">
                      {filters.is_read === "false"
                        ? "Unread Notifications"
                        : filters.is_read === "true"
                        ? "Read Notifications"
                        : "All Notifications"}
                    </h3>
                    <span className="text-sm text-gray-600">
                      Showing {notifications.length} of {pagination.count || 0}
                    </span>
                  </div>
                </div>

                {/* Notifications Items */}
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onUpdate={handleNotificationUpdate}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.maxPages > 1 && (
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Page {pagination.pageNumber} of {pagination.maxPages}
                      </span>
                      <div className="flex items-center space-x-2">
                        <Button
                          disabled={pagination.pageNumber <= 1}
                          onClick={() =>
                            handleFilterChange(
                              "pageNumber",
                              filters.pageNumber - 1
                            )
                          }
                          size="small"
                        >
                          Previous
                        </Button>
                        <Button
                          disabled={
                            pagination.pageNumber >= pagination.maxPages
                          }
                          onClick={() =>
                            handleFilterChange(
                              "pageNumber",
                              filters.pageNumber + 1
                            )
                          }
                          size="small"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Empty State
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <div className="w-24 h-24 bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mb-6">
                  <Bell className="h-12 w-12 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {filters.is_read === "false"
                    ? "No unread notifications"
                    : filters.is_read === "true"
                    ? "No read notifications"
                    : "No notifications yet"}
                </h3>
                <p className="text-gray-600 text-center mb-6 max-w-md">
                  {filters.is_read === "false"
                    ? "You're all caught up! All your notifications have been read."
                    : filters.is_read === "true"
                    ? "You haven't read any notifications yet."
                    : "We'll notify you here when you have updates about your orders, payments, and temple services."}
                </p>

                {filters.is_read !== "all" && (
                  <Button
                    type="primary"
                    onClick={() => handleFilterChange("is_read", "all")}
                    className="bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600"
                  >
                    View All Notifications
                  </Button>
                )}

                {filters.is_read === "all" && (
                  <Button
                    onClick={() => navigate("/")}
                    className="border-orange-500 text-orange-600 hover:bg-orange-50"
                  >
                    Continue Booking Services
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default NotificationsPage;
